import { useState, useMemo, useCallback, memo } from 'react';
import { Lock, Unlock, ChevronDown, ChevronsDown, ChevronsUp } from 'lucide-react';

type Permission = {
  id_feature: string;
  title: string;
  name: string;
};

type PermissionListProps = {
  permissions: Permission[];
  selectedPermissions: string[];
  onPermissionChange: (permission: Permission, checked: boolean) => void;
};

const categorys: any = {
  'cashback': 'Sistema de Cashback',
  'logs': 'Sistema de Logs',
  'ai': 'Inteligência Artificial',
  'users': 'Controle de Usuários',
  'nps': 'Gerenciador de Enquetes',
  'campaign': 'Simulador de Campanhas',
  'conecta': 'Conecta',
  'datatransfer': 'Data Transfer',
  'reports': 'Relatórios',
  'api': 'API',
  'seller': 'Seller Mapping',
  'infrapay': 'Infrapay',
  'devops': 'DevOps',
  'settings': 'Configurações',
};

const categoryIcons: any = {
  'cashback': '💰',
  'logs': '📋',
  'ai': '🤖',
  'users': '👥',
  'nps': '📊',
  'campaign': '🎯',
  'conecta': '🔗',
  'datatransfer': '📦',
  'reports': '📈',
  'api': '🔌',
  'seller': '🛒',
  'infrapay': '💳',
  'devops': '⚙️',
  'settings': '🔧',
};

const PermissionItem = memo(({
  permission,
  isChecked,
  onPermissionChange,
  isFirst
}: {
  permission: Permission;
  isChecked: boolean;
  onPermissionChange: (permission: Permission, checked: boolean) => void;
  isFirst: boolean;
}) => {
  return (
    <label
      className={`flex items-center px-6 py-4 hover:bg-blue-50 transition-colors cursor-pointer group ${
        isFirst ? 'border-t border-gray-200' : ''
      }`}
    >
      <div className="flex items-center gap-4 w-full">
        <div className="relative flex items-center justify-center">
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => onPermissionChange(permission, e.target.checked)}
            className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-200 focus:ring-offset-0 cursor-pointer transition-all"
          />
        </div>
        <div className="flex-1">
          <span className={`text-sm font-medium transition-colors ${
            isChecked
              ? 'text-blue-700'
              : 'text-gray-700 group-hover:text-blue-600'
          }`}>
            {permission.title}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isChecked ? (
            <div className="flex items-center gap-2 px-3 py-1 bg-green-100 rounded-full">
              <Unlock className="w-4 h-4 text-green-600" />
              <span className="text-xs font-semibold text-green-700">Ativo</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
              <Lock className="w-4 h-4 text-gray-500" />
              <span className="text-xs font-medium text-gray-600">Inativo</span>
            </div>
          )}
        </div>
      </div>
    </label>
  );
});

PermissionItem.displayName = 'PermissionItem';

const CategorySection = memo(({
  category,
  perms,
  isExpanded,
  selectedCount,
  totalCount,
  allSelected,
  someSelected,
  selectedSet,
  onToggleCategory,
  onPermissionChange
}: {
  category: string;
  perms: Permission[];
  isExpanded: boolean;
  selectedCount: number;
  totalCount: number;
  allSelected: boolean;
  someSelected: boolean;
  selectedSet: Set<string>;
  onToggleCategory: (category: string) => void;
  onPermissionChange: (permission: Permission, checked: boolean) => void;
}) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden transition-shadow duration-200 hover:shadow-md">
      <button
        onClick={() => onToggleCategory(category)}
        className="w-full bg-gradient-to-r px-6 py-4 flex items-center justify-between hover:from-gray-100 hover:to-gray-200 transition-colors duration-200"
      >
        <div className="flex items-center gap-3">
          <div className="text-2xl">{categoryIcons[category] || '📁'}</div>
          <div className="flex flex-col items-start">
            <h3 className="text-lg font-bold text-gray-800">
              {categorys[category] || category}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-gray-500">
                {totalCount} {totalCount === 1 ? 'permissão' : 'permissões'}
              </span>
              {selectedCount > 0 && (
                <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                  allSelected
                    ? 'bg-green-100 text-green-700'
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {selectedCount} selecionada{selectedCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {someSelected && (
            <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
          )}
          {allSelected && (
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          )}
          <div className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}>
            <ChevronDown className="w-5 h-5 text-gray-600" />
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="divide-y divide-gray-100 bg-white">
          {perms.map((permission, index) => (
            <PermissionItem
              key={permission.id_feature}
              permission={permission}
              isChecked={selectedSet.has(permission.id_feature)}
              onPermissionChange={onPermissionChange}
              isFirst={index === 0}
            />
          ))}
        </div>
      )}
    </div>
  );
});

CategorySection.displayName = 'CategorySection';

export const PermissionList = ({
  permissions,
  selectedPermissions,
  onPermissionChange,
}: PermissionListProps) => {
  const groupedPermissions = useMemo(() => {
    return permissions.reduce((acc, permission) => {
      const category = permission.name.split('-')[0];
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(permission);
      return acc;
    }, {} as Record<string, Permission[]>);
  }, [permissions]);

  const categories = useMemo(() => Object.keys(groupedPermissions), [groupedPermissions]);

  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>(() =>
    categories.reduce((acc, category) => {
      acc[category] = true;
      return acc;
    }, {} as Record<string, boolean>)
  );

  const selectedSet = useMemo(() => new Set(selectedPermissions), [selectedPermissions]);

  const categoryStats = useMemo(() => {
    return categories.reduce((acc, category) => {
      const perms = groupedPermissions[category];
      const selectedCount = perms.filter(p => selectedSet.has(p.id_feature)).length;
      acc[category] = {
        total: perms.length,
        selected: selectedCount,
        allSelected: selectedCount === perms.length,
        someSelected: selectedCount > 0 && selectedCount < perms.length,
      };
      return acc;
    }, {} as Record<string, { total: number; selected: number; allSelected: boolean; someSelected: boolean }>);
  }, [categories, groupedPermissions, selectedSet]);

  const toggleCategory = useCallback((category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  }, []);

  const toggleAll = useCallback((expand: boolean) => {
    setExpandedCategories(
      categories.reduce((acc, category) => {
        acc[category] = expand;
        return acc;
      }, {} as Record<string, boolean>)
    );
  }, [categories]);

  const allExpanded = useMemo(() =>
    categories.every(cat => expandedCategories[cat]),
    [expandedCategories, categories]
  );

  const allCollapsed = useMemo(() =>
    categories.every(cat => !expandedCategories[cat]),
    [expandedCategories, categories]
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2 mb-4">
        <button
          onClick={() => toggleAll(true)}
          disabled={allExpanded}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronsDown className="w-4 h-4" />
          Expandir Todos
        </button>
        <button
          onClick={() => toggleAll(false)}
          disabled={allCollapsed}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronsUp className="w-4 h-4" />
          Colapsar Todos
        </button>
      </div>

      {categories.map((category) => {
        const stats = categoryStats[category];
        return (
          <CategorySection
            key={category}
            category={category}
            perms={groupedPermissions[category]}
            isExpanded={expandedCategories[category]}
            selectedCount={stats.selected}
            totalCount={stats.total}
            allSelected={stats.allSelected}
            someSelected={stats.someSelected}
            selectedSet={selectedSet}
            onToggleCategory={toggleCategory}
            onPermissionChange={onPermissionChange}
          />
        );
      })}
    </div>
  );
};
